import {
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { fetchFirebaseAuthUser, FirebaseAuthUser, setUserRoles } from "../api/backoffice.api";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { baseUrl as serverBaseUrl, getAuthenticatedServerClient } from "../api/server.api";

export interface AutovioEmployee {
  readonly id: string;
  readonly displayName: string;
  readonly avatarUrl?: string;
  readonly roles: Array<Role>;
  readonly entitledForDrivingSchools: Array<string>;
}

function firebaseAuthUserToAutovioEmployee(user: FirebaseAuthUser): AutovioEmployee {
  const email = user.providerData.find((it) => it.email)?.email;
  return {
    id: user.uid,
    displayName: user.displayName || user.providerData.find((it) => it.displayName)?.displayName || email || user.uid,
    avatarUrl: `${serverBaseUrl}/user/${user.uid}/avatar`,
    roles: (user.customClaims?.roles ?? []).sort(),
    entitledForDrivingSchools: user.customClaims?.entitledForDrivingSchools ?? [],
  };
}

class AutovioEmployeesProvider extends AbstractDataProvider<AutovioEmployee> {
  async getOne(_resource: string, { id }: GetOneParams): Promise<GetOneResult<AutovioEmployee>> {
    const record = await fetchFirebaseAuthUser(id);
    return { data: firebaseAuthUserToAutovioEmployee(record) };
  }

  async getMany(resource: string, { ids }: GetManyParams): Promise<GetManyResult<AutovioEmployee>> {
    return { data: [(await this.getOne(resource, { id: ids[0] })).data] };
  }

  async getList(
    resource: string,
    { sort, filter, pagination }: GetListParams,
  ): Promise<GetListResult<AutovioEmployee>> {
    const serverClient = await getAuthenticatedServerClient();
    const autovioStaff = await serverClient.getAutovioStaff();
    const records = await Promise.all(
      autovioStaff.map(async ({ firebaseAuthUserUid: id }) => this.getOne(resource, { id }).then(({ data }) => data)),
    );
    return applyPagination(applySort(applyFilter(records, filter), sort), pagination);
  }

  async update(resource: string, { id, data }: UpdateParams<AutovioEmployee>): Promise<UpdateResult<AutovioEmployee>> {
    if (!Array.isArray(data.roles)) {
      throw new Error("data.roles must be an array");
    }
    await setUserRoles(id.toString(), data.roles, data.entitledForDrivingSchools);
    return await this.getOne(resource, { id });
  }
}

export const autovioEmployeesProvider = new AutovioEmployeesProvider();
